import { useState } from "react";
import { Eye } from "lucide-react";
import { useUserContext } from "../context/UserContext";
import { isUserSetUp } from "../utils/userUtils";

interface GodModeActivatorProps {
  targetUser: { userId: string; retailers?: string[] };
  onSuccess?: () => void;
  /** Icon-only button for dense contexts like table rows. */
  compact?: boolean;
}

export default function GodModeActivator({
  targetUser,
  onSuccess,
  compact = false,
}: GodModeActivatorProps) {
  const { activateGodMode, isGodMode } = useUserContext();
  const [isActivating, setIsActivating] = useState(false);

  const handleActivateGodMode = async () => {
    setIsActivating(true);
    try {
      await activateGodMode(targetUser.userId);
      window.location.href = "/";
      onSuccess?.();
    } catch (error) {
      console.error("Failed to activate god mode:", error);
      alert("Failed to activate god mode");
    } finally {
      setIsActivating(false);
    }
  };

  // Don't show the button if already in god mode
  if (isGodMode) {
    return null;
  }

  if (!isUserSetUp(targetUser)) {
    return null;
  }

  if (compact) {
    return (
      <button
        onPointerDown={handleActivateGodMode}
        disabled={isActivating}
        title="View as user"
        className="p-1 rounded-md text-blue-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/25 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Eye size={14} />
      </button>
    );
  }

  return (
    <button
      onPointerDown={handleActivateGodMode}
      disabled={isActivating}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-600 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <Eye className="w-3 h-3" />
      {isActivating ? "Activating..." : "View as User"}
    </button>
  );
}
